import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import { 
  AlertCircle, Check, CreditCard, Crown, Zap, Building2, Loader2, AlertTriangle, 
  Calendar, Receipt, MapPin, FileText, Search, QrCode, Copy, ExternalLink,
  Banknote, Smartphone, Users, TrendingUp, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { validateCNPJ, maskCNPJ, cleanCNPJ, maskCEP, cleanCEP, fetchAddressByCEP } from '../utils/brazilValidation';
import { trackSubscriptionConversion } from '../utils/googleAdsTracking';

const API = process.env.REACT_APP_BACKEND_URL;

const ESTADOS_BR = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG',
  'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
];

export default function Subscription() {
  const [plans, setPlans] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);
  const [activeTab, setActiveTab] = useState('plans');
  const [billingCycle, setBillingCycle] = useState('monthly'); // monthly or annual
  
  // Employee limit status
  const [employeeLimitStatus, setEmployeeLimitStatus] = useState(null);
  
  // Payment method modal
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('PIX');
  
  // PIX payment result
  const [showPixModal, setShowPixModal] = useState(false);
  const [pixData, setPixData] = useState(null);
  
  // Credit Card data
  const [creditCardData, setCreditCardData] = useState({
    holderName: '',
    number: '',
    expiryMonth: '',
    expiryYear: '',
    ccv: ''
  });
  const [creditCardHolderInfo, setCreditCardHolderInfo] = useState({
    name: '',
    email: '',
    cpfCnpj: '',
    postalCode: '',
    addressNumber: '',
    phone: ''
  });
  
  // Financial data states
  const [showFinancialModal, setShowFinancialModal] = useState(false);
  const [selectedPlanForPurchase, setSelectedPlanForPurchase] = useState(null);
  const [financialData, setFinancialData] = useState({
    endereco_logradouro: '',
    endereco_numero: '',
    endereco_complemento: '',
    endereco_bairro: '',
    endereco_cidade: '',
    endereco_estado: '',
    endereco_cep: '',
    inscricao_estadual: '',
    inscricao_estadual_isento: false,
    inscricao_municipal: '',
    razao_social: '',
    nome_fantasia: '',
    cnpj: '',
    email_nf: ''
  });
  const [financialDataComplete, setFinancialDataComplete] = useState(false);
  const [savingFinancial, setSavingFinancial] = useState(false);
  const [loadingCEP, setLoadingCEP] = useState(false);
  const [cnpjError, setCnpjError] = useState('');
  const [cepError, setCepError] = useState('');

  const fetchEmployeeLimitStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/employees/limit-status`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setEmployeeLimitStatus(data);
      }
    } catch (error) {
      console.error('Error fetching employee limit:', error);
    }
  }, []);

  const fetchFinancialData = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/company/financial-data`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setFinancialDataComplete(data.dados_financeiros_completos);
        if (data.dados_financeiros) {
          setFinancialData(prev => ({
            ...prev,
            ...data.dados_financeiros
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching financial data:', error);
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const fetchOptions = { credentials: 'include' };

      // Fetch plans from Asaas endpoint
      const [plansRes, subRes, paymentsRes] = await Promise.all([
        fetch(`${API}/api/asaas/plans`),
        fetch(`${API}/api/subscription`, fetchOptions),
        fetch(`${API}/api/asaas/payments`, fetchOptions)
      ]);

      if (plansRes.ok) {
        const plansData = await plansRes.json();
        setPlans(plansData.plans || []);
      }

      if (subRes.ok) {
        const subData = await subRes.json();
        setCurrentSubscription(subData);
      }

      if (paymentsRes.ok) {
        const paymentsData = await paymentsRes.json();
        setPaymentHistory(paymentsData || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchFinancialData();
    fetchEmployeeLimitStatus();
  }, [fetchData, fetchFinancialData, fetchEmployeeLimitStatus]);

  const handleSelectPlan = async (planId) => {
    // Check if financial data is complete
    if (!financialDataComplete) {
      setSelectedPlanForPurchase(planId);
      setShowFinancialModal(true);
      return;
    }
    
    // Show payment method selection
    setSelectedPlan(planId);
    setShowPaymentModal(true);
  };

  const processPayment = async () => {
    if (!selectedPlan) return;
    
    // Validate credit card data if payment method is credit card
    if (paymentMethod === 'CREDIT_CARD') {
      if (!creditCardData.holderName || !creditCardData.number || 
          !creditCardData.expiryMonth || !creditCardData.expiryYear || !creditCardData.ccv) {
        toast.error('Preencha todos os dados do cartão');
        return;
      }
      if (!creditCardHolderInfo.name || !creditCardHolderInfo.cpfCnpj || 
          !creditCardHolderInfo.postalCode || !creditCardHolderInfo.phone) {
        toast.error('Preencha todos os dados do titular');
        return;
      }
    }
    
    setProcessingPlan(selectedPlan);
    setShowPaymentModal(false);
    
    try {
      const requestBody = { 
        plan_id: selectedPlan,
        billing_type: paymentMethod,
        cycle: billingCycle
      };
      
      // Add credit card data if payment method is credit card
      if (paymentMethod === 'CREDIT_CARD') {
        requestBody.credit_card = {
          holder_name: creditCardData.holderName,
          number: creditCardData.number.replace(/\s/g, ''),
          expiry_month: creditCardData.expiryMonth,
          expiry_year: creditCardData.expiryYear,
          ccv: creditCardData.ccv
        };
        requestBody.credit_card_holder_info = {
          name: creditCardHolderInfo.name,
          email: creditCardHolderInfo.email || financialData.email_nf,
          cpf_cnpj: creditCardHolderInfo.cpfCnpj.replace(/\D/g, ''),
          postal_code: creditCardHolderInfo.postalCode.replace(/\D/g, ''),
          address_number: creditCardHolderInfo.addressNumber,
          phone: creditCardHolderInfo.phone.replace(/\D/g, '')
        };
      }
      
      const response = await fetch(`${API}/api/asaas/checkout`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        const data = await response.json();
        
        if (paymentMethod === 'PIX') {
          // Track conversion for PIX generation (payment intent)
          const transactionId = data.payment?.id || `pix_${Date.now()}`;
          const planValue = plans.find(p => p.id === selectedPlan)?.price || 1.0;
          trackSubscriptionConversion(transactionId, planValue);
          
          // Show PIX QR Code modal
          setPixData({
            qrCode: data.payment.pix_qr_code,
            copyPaste: data.payment.pix_copy_paste,
            value: data.payment.value,
            planName: data.payment.plan_name
          });
          setShowPixModal(true);
        } else if (paymentMethod === 'BOLETO') {
          // Track conversion for boleto generation (payment intent)
          const transactionId = data.payment?.id || `boleto_${Date.now()}`;
          const planValue = plans.find(p => p.id === selectedPlan)?.price || 1.0;
          trackSubscriptionConversion(transactionId, planValue);
          
          // Open boleto URL
          if (data.payment.bank_slip_url) {
            window.open(data.payment.bank_slip_url, '_blank');
            toast.success('Boleto gerado! Abra a nova aba para visualizar.');
          } else if (data.payment.invoice_url) {
            window.open(data.payment.invoice_url, '_blank');
            toast.success('Boleto gerado!');
          }
        } else if (paymentMethod === 'CREDIT_CARD') {
          // Credit card payment processed
          toast.success('Pagamento com cartão processado com sucesso!');
          
          // Track Google Ads conversion
          const transactionId = data.payment?.id || data.subscription_id;
          const planValue = plans.find(p => p.id === selectedPlan)?.price || 1.0;
          trackSubscriptionConversion(transactionId, planValue);
          
          // Reset credit card form
          setCreditCardData({
            holderName: '',
            number: '',
            expiryMonth: '',
            expiryYear: '',
            ccv: ''
          });
        }
        
        // Refresh payment history
        fetchData();
        toast.success(data.message || 'Pagamento criado com sucesso!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao processar pagamento');
      }
    } catch (error) {
      console.error('Error creating payment:', error);
      toast.error('Erro ao processar pagamento');
    } finally {
      setProcessingPlan(null);
      setSelectedPlan(null);
    }
  };

  const copyPixCode = () => {
    if (pixData?.copyPaste) {
      navigator.clipboard.writeText(pixData.copyPaste);
      toast.success('Código PIX copiado!');
    }
  };

  const handleSaveFinancialData = async () => {
    const required = ['endereco_logradouro', 'endereco_numero', 'endereco_bairro', 
                      'endereco_cidade', 'endereco_estado', 'endereco_cep', 'razao_social'];
    
    for (const field of required) {
      if (!financialData[field]) {
        toast.error('Preencha todos os campos obrigatórios');
        return;
      }
    }
    
    if (!financialData.cnpj || cleanCNPJ(financialData.cnpj).length !== 14) {
      toast.error('Informe um CNPJ válido');
      return;
    }
    
    if (!validateCNPJ(financialData.cnpj)) {
      toast.error('CNPJ inválido. Verifique os dígitos.');
      return;
    }
    
    if (!financialData.email_nf || !financialData.email_nf.includes('@')) {
      toast.error('Informe um e-mail válido para envio da nota fiscal');
      return;
    }
    
    if (!financialData.inscricao_estadual_isento && !financialData.inscricao_estadual) {
      toast.error('Informe a Inscrição Estadual ou marque como Isento');
      return;
    }
    
    setSavingFinancial(true);
    try {
      const response = await fetch(`${API}/api/company/financial-data`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(financialData)
      });
      
      if (response.ok) {
        toast.success('Dados financeiros salvos com sucesso');
        setFinancialDataComplete(true);
        setShowFinancialModal(false);
        
        if (selectedPlanForPurchase) {
          setSelectedPlan(selectedPlanForPurchase);
          setShowPaymentModal(true);
          setSelectedPlanForPurchase(null);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao salvar dados');
      }
    } catch (error) {
      console.error('Error saving financial data:', error);
      toast.error('Erro ao salvar dados financeiros');
    } finally {
      setSavingFinancial(false);
    }
  };

  const handleFinancialInputChange = (field, value) => {
    setFinancialData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCNPJChange = (value) => {
    const masked = maskCNPJ(value);
    setFinancialData(prev => ({ ...prev, cnpj: masked }));
    
    const cleaned = cleanCNPJ(value);
    if (cleaned.length === 14) {
      if (!validateCNPJ(cleaned)) {
        setCnpjError('CNPJ inválido');
      } else {
        setCnpjError('');
      }
    } else if (cleaned.length > 0) {
      setCnpjError('');
    }
  };

  const handleCEPChange = async (value) => {
    const masked = maskCEP(value);
    setFinancialData(prev => ({ ...prev, endereco_cep: masked }));
    setCepError('');
    
    const cleaned = cleanCEP(value);
    if (cleaned.length === 8) {
      setLoadingCEP(true);
      const result = await fetchAddressByCEP(cleaned);
      setLoadingCEP(false);
      
      if (result.success) {
        setFinancialData(prev => ({
          ...prev,
          endereco_logradouro: result.logradouro || prev.endereco_logradouro,
          endereco_bairro: result.bairro || prev.endereco_bairro,
          endereco_cidade: result.cidade || prev.endereco_cidade,
          endereco_estado: result.estado || prev.endereco_estado,
          endereco_complemento: result.complemento || prev.endereco_complemento
        }));
        toast.success('Endereço preenchido automaticamente!');
      } else if (result.error) {
        setCepError(result.error);
      }
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Tem certeza que deseja cancelar sua assinatura? Você ainda terá acesso até o fim do período pago.')) {
      return;
    }

    try {
      const response = await fetch(`${API}/api/subscription/cancel`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Assinatura cancelada com sucesso');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao cancelar assinatura');
      }
    } catch (error) {
      console.error('Error cancelling subscription:', error);
    }
  };

  const getPlanIcon = (planId) => {
    switch (planId) {
      case 'starter': return <Zap className="h-6 w-6" />;
      case 'professional': return <Crown className="h-6 w-6" />;
      case 'enterprise': return <Building2 className="h-6 w-6" />;
      case 'master': return <Building2 className="h-6 w-6" />;
      default: return <Zap className="h-6 w-6" />;
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      trial: { label: 'Período de Teste', variant: 'warning', icon: AlertTriangle },
      active: { label: 'Ativo', variant: 'success', icon: Check },
      pending: { label: 'Pendente', variant: 'secondary', icon: Loader2 },
      cancelled: { label: 'Cancelado', variant: 'destructive', icon: AlertCircle },
      expired: { label: 'Expirado', variant: 'destructive', icon: AlertCircle },
      overdue: { label: 'Em Atraso', variant: 'destructive', icon: AlertTriangle }
    };
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const getPaymentStatusBadge = (status) => {
    const statusConfig = {
      CONFIRMED: { label: 'Confirmado', className: 'bg-green-100 text-green-800' },
      RECEIVED: { label: 'Recebido', className: 'bg-green-100 text-green-800' },
      PENDING: { label: 'Pendente', className: 'bg-yellow-100 text-yellow-800' },
      OVERDUE: { label: 'Vencido', className: 'bg-red-100 text-red-800' },
      REFUNDED: { label: 'Reembolsado', className: 'bg-blue-100 text-blue-800' },
      CANCELLED: { label: 'Cancelado', className: 'bg-gray-100 text-gray-800' },
      approved: { label: 'Aprovado', className: 'bg-green-100 text-green-800' },
      pending: { label: 'Pendente', className: 'bg-yellow-100 text-yellow-800' },
      rejected: { label: 'Rejeitado', className: 'bg-red-100 text-red-800' }
    };
    const config = statusConfig[status] || { label: status, className: 'bg-gray-100 text-gray-800' };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.className}`}>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-[#0d9488]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="subscription-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1e3a5f]" style={{ fontFamily: 'Manrope, sans-serif' }}>Assinatura</h1>
          <p className="text-gray-500">Gerencie seu plano e pagamentos</p>
        </div>
      </div>

      {/* Employee Limit Status Banner */}
      {employeeLimitStatus && !employeeLimitStatus.unlimited && (
        <Card className={`border-l-4 ${
          !employeeLimitStatus.can_add 
            ? 'border-l-red-500 bg-red-50' 
            : employeeLimitStatus.current_count >= employeeLimitStatus.limit * 0.8
            ? 'border-l-amber-500 bg-amber-50'
            : 'border-l-[#0d9488] bg-[#e8f4fc]'
        }`}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  !employeeLimitStatus.can_add 
                    ? 'bg-red-100' 
                    : employeeLimitStatus.current_count >= employeeLimitStatus.limit * 0.8
                    ? 'bg-amber-100'
                    : 'bg-[#0d9488]/10'
                }`}>
                  <Users className={`h-6 w-6 ${
                    !employeeLimitStatus.can_add 
                      ? 'text-red-600' 
                      : employeeLimitStatus.current_count >= employeeLimitStatus.limit * 0.8
                      ? 'text-amber-600'
                      : 'text-[#0d9488]'
                  }`} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className={`font-semibold ${
                      !employeeLimitStatus.can_add ? 'text-red-800' : 'text-[#1e3a5f]'
                    }`}>
                      {!employeeLimitStatus.can_add 
                        ? 'Limite do Plano Atingido!'
                        : 'Uso de Funcionários'
                      }
                    </p>
                    {!employeeLimitStatus.can_add && (
                      <Badge className="bg-red-100 text-red-700">Fazer Upgrade</Badge>
                    )}
                  </div>
                  <p className="text-sm text-gray-600">
                    {employeeLimitStatus.current_count} de {employeeLimitStatus.limit} funcionários utilizados 
                    (Plano {employeeLimitStatus.plan_name})
                  </p>
                  <div className="mt-2 w-64">
                    <Progress 
                      value={(employeeLimitStatus.current_count / employeeLimitStatus.limit) * 100} 
                      className={`h-2 ${
                        !employeeLimitStatus.can_add 
                          ? '[&>div]:bg-red-500' 
                          : employeeLimitStatus.current_count >= employeeLimitStatus.limit * 0.8
                          ? '[&>div]:bg-amber-500'
                          : '[&>div]:bg-[#0d9488]'
                      }`}
                    />
                  </div>
                </div>
              </div>
              {!employeeLimitStatus.can_add && (
                <Button 
                  onClick={() => setActiveTab('plans')}
                  className="bg-red-600 hover:bg-red-700"
                >
                  <TrendingUp className="h-4 w-4 mr-2" />
                  Fazer Upgrade
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Current Subscription Status */}
      {currentSubscription && (
        <Card className="border-l-4 border-l-[#0d9488]">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {getPlanIcon(currentSubscription.plan)}
                <div>
                  <CardTitle>Plano {currentSubscription.plan_details?.name || currentSubscription.plan}</CardTitle>
                  <CardDescription>
                    {currentSubscription.status === 'trial' 
                      ? `Teste grátis até ${new Date(currentSubscription.trial_end_date).toLocaleDateString('pt-BR')}`
                      : currentSubscription.status === 'active'
                      ? `Próxima renovação: ${new Date(currentSubscription.renewal_date).toLocaleDateString('pt-BR')}`
                      : 'Sua assinatura precisa de atenção'}
                  </CardDescription>
                </div>
              </div>
              {getStatusBadge(currentSubscription.status)}
            </div>
          </CardHeader>
          {currentSubscription.status === 'trial' && (
            <CardContent>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800">Período de teste ativo</p>
                  <p className="text-sm text-amber-700">
                    Você tem acesso gratuito por 7 dias para testar todas as funcionalidades. 
                    Escolha um plano abaixo para continuar usando após o período de teste.
                  </p>
                </div>
              </div>
            </CardContent>
          )}
          {currentSubscription.status === 'expired' && (
            <CardContent>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-red-500 mt-0.5" />
                <div>
                  <p className="font-medium text-red-800">Assinatura expirada</p>
                  <p className="text-sm text-red-700">
                    Seu período de teste acabou. Escolha um plano abaixo para continuar tendo acesso ao sistema.
                  </p>
                </div>
              </div>
            </CardContent>
          )}
          {currentSubscription.status === 'active' && (
            <CardFooter>
              <Button variant="outline" onClick={handleCancelSubscription} className="text-red-600 hover:text-red-700">
                Cancelar assinatura
              </Button>
            </CardFooter>
          )}
        </Card>
      )}

      {/* Tabs */}
      <div className="border-b">
        <div className="flex gap-4">
          <button
            onClick={() => setActiveTab('plans')}
            className={`pb-3 px-1 font-medium transition-colors ${
              activeTab === 'plans' 
                ? 'text-[#0d9488] border-b-2 border-[#0d9488]' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Planos
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`pb-3 px-1 font-medium transition-colors ${
              activeTab === 'history' 
                ? 'text-[#0d9488] border-b-2 border-[#0d9488]' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Histórico de Pagamentos
          </button>
        </div>
      </div>

      {/* Billing Cycle Toggle */}
      {activeTab === 'plans' && (
        <div className="flex items-center justify-center gap-4 p-4 bg-[#e8f4fc] rounded-xl">
          <span className={`text-sm font-medium ${billingCycle === 'monthly' ? 'text-[#1e3a5f]' : 'text-gray-500'}`}>
            Mensal
          </span>
          <button
            onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'annual' : 'monthly')}
            className={`relative w-14 h-7 rounded-full transition-colors ${
              billingCycle === 'annual' ? 'bg-[#0d9488]' : 'bg-gray-300'
            }`}
          >
            <div className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-transform ${
              billingCycle === 'annual' ? 'translate-x-8' : 'translate-x-1'
            }`} />
          </button>
          <span className={`text-sm font-medium ${billingCycle === 'annual' ? 'text-[#1e3a5f]' : 'text-gray-500'}`}>
            Anual
          </span>
          {billingCycle === 'annual' && (
            <Badge className="bg-emerald-100 text-emerald-700">5% de desconto</Badge>
          )}
        </div>
      )}

      {/* Plans Tab */}
      {activeTab === 'plans' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map((plan) => {
            const isCurrentPlan = currentSubscription?.plan === plan.id;
            const isActive = currentSubscription?.status === 'active';
            const price = billingCycle === 'annual' ? plan.price_monthly_if_annual : plan.price_monthly;
            const totalPrice = billingCycle === 'annual' ? plan.price_annual : plan.price_monthly;
            
            return (
              <Card 
                key={plan.id} 
                className={`relative ${isCurrentPlan ? 'ring-2 ring-[#0d9488]' : ''} ${
                  plan.id === 'master' ? 'border-amber-200 shadow-lg' : 
                  plan.id === 'professional' ? 'border-[#0d9488]/30 shadow-lg' : ''
                }`}
                data-testid={`plan-card-${plan.id}`}
              >
                {plan.id === 'master' && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-amber-600">Multi-CNPJ</Badge>
                  </div>
                )}
                {plan.id === 'professional' && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-[#0d9488]">Mais Popular</Badge>
                  </div>
                )}
                {isCurrentPlan && (
                  <div className="absolute -top-3 right-4">
                    <Badge variant="secondary">Plano Atual</Badge>
                  </div>
                )}
                <CardHeader className="text-center pb-2">
                  <div className={`mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-2 ${
                    plan.id === 'starter' ? 'bg-emerald-100 text-emerald-600' :
                    plan.id === 'professional' ? 'bg-[#0d9488]/10 text-[#0d9488]' :
                    plan.id === 'enterprise' ? 'bg-purple-100 text-purple-600' :
                    'bg-amber-100 text-amber-600'
                  }`}>
                    {getPlanIcon(plan.id)}
                  </div>
                  <CardTitle>{plan.name}</CardTitle>
                  <div className="mt-2">
                    <span className="text-3xl font-bold text-[#1e3a5f]">
                      R$ {price.toFixed(2).replace('.', ',')}
                    </span>
                    <span className="text-gray-500">/mês</span>
                  </div>
                  {billingCycle === 'annual' && (
                    <p className="text-sm text-gray-500 mt-1">
                      Total: R$ {totalPrice.toFixed(2).replace('.', ',')}/ano
                    </p>
                  )}
                  <CardDescription className="mt-2">
                    {plan.employees_label}
                    {plan.multi_cnpj && (
                      <Badge className="ml-2 bg-amber-100 text-amber-700 text-xs">Multi-Empresa</Badge>
                    )}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <Check className="h-5 w-5 text-[#0d9488] shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <CardFooter>
                  <Button 
                    className={`w-full ${
                      plan.id === 'professional' || plan.id === 'master' 
                        ? 'bg-[#0d9488] hover:bg-[#0f766e]' 
                        : ''
                    }`}
                    variant={plan.id === 'professional' || plan.id === 'master' ? 'default' : 'outline'}
                    disabled={isCurrentPlan && isActive || processingPlan === plan.id}
                    onClick={() => handleSelectPlan(plan.id)}
                    data-testid={`select-plan-${plan.id}`}
                  >
                    {processingPlan === plan.id ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Processando...
                      </>
                    ) : isCurrentPlan && isActive ? (
                      'Plano Atual'
                    ) : (
                      <>
                        <CreditCard className="h-4 w-4 mr-2" />
                        {isCurrentPlan ? 'Renovar Plano' : 'Assinar Plano'}
                      </>
                    )}
                  </Button>
                </CardFooter>
              </Card>
            );
          })}
        </div>
      )}

      {/* Payment History Tab */}
      {activeTab === 'history' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5 text-[#0d9488]" />
              Histórico de Pagamentos
            </CardTitle>
          </CardHeader>
          <CardContent>
            {paymentHistory.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Receipt className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>Nenhum pagamento realizado ainda</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Data</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Descrição</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Método</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Valor</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paymentHistory.map((payment) => (
                      <tr key={payment.id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-gray-400" />
                            {new Date(payment.created_at).toLocaleDateString('pt-BR')}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          Plano {payment.plan_name || payment.plan?.charAt(0).toUpperCase() + payment.plan?.slice(1) || 'N/A'}
                          {payment.cycle && (
                            <span className="text-xs text-gray-500 ml-2">
                              ({payment.cycle === 'annual' ? 'Anual' : 'Mensal'})
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-1">
                            {payment.billing_type === 'PIX' ? (
                              <QrCode className="h-4 w-4 text-[#0d9488]" />
                            ) : payment.billing_type === 'BOLETO' ? (
                              <Banknote className="h-4 w-4 text-orange-500" />
                            ) : (
                              <CreditCard className="h-4 w-4 text-blue-500" />
                            )}
                            <span className="text-sm">{payment.billing_type || 'N/A'}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 font-medium">
                          R$ {(payment.value || payment.amount || 0).toFixed(2).replace('.', ',')}
                        </td>
                        <td className="py-3 px-4">
                          {getPaymentStatusBadge(payment.status)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Payment Info */}
      <Card className="bg-gradient-to-r from-[#e8f4fc] to-white">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-[#0d9488]/10 rounded-lg">
              <CreditCard className="h-5 w-5 text-[#0d9488]" />
            </div>
            <div>
              <p className="font-medium text-[#1e3a5f]">Pagamento Seguro via Asaas</p>
              <p className="text-sm text-gray-600 mt-1">
                Aceitamos PIX (pagamento instantâneo), Boleto Bancário e Cartão de Crédito. 
                Seus dados são protegidos com criptografia de ponta a ponta.
              </p>
              <div className="flex items-center gap-4 mt-3">
                <div className="flex items-center gap-1 text-sm text-gray-500">
                  <QrCode className="h-4 w-4 text-[#0d9488]" />
                  PIX
                </div>
                <div className="flex items-center gap-1 text-sm text-gray-500">
                  <Banknote className="h-4 w-4 text-orange-500" />
                  Boleto
                </div>
                <div className="flex items-center gap-1 text-sm text-gray-500">
                  <CreditCard className="h-4 w-4 text-blue-500" />
                  Cartão
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Payment Method Selection Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className={paymentMethod === 'CREDIT_CARD' ? 'max-w-lg max-h-[90vh] overflow-y-auto' : 'max-w-md'}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-[#0d9488]" />
              Escolha a Forma de Pagamento
            </DialogTitle>
            <DialogDescription>
              Selecione como deseja pagar pelo plano {plans.find(p => p.id === selectedPlan)?.name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-4">
            <button
              onClick={() => setPaymentMethod('PIX')}
              className={`w-full p-4 rounded-xl border-2 transition-all flex items-center gap-4 ${
                paymentMethod === 'PIX' 
                  ? 'border-[#0d9488] bg-[#0d9488]/5' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                paymentMethod === 'PIX' ? 'bg-[#0d9488]' : 'bg-gray-100'
              }`}>
                <QrCode className={`h-6 w-6 ${paymentMethod === 'PIX' ? 'text-white' : 'text-gray-500'}`} />
              </div>
              <div className="text-left flex-1">
                <p className="font-medium text-[#1e3a5f]">PIX</p>
                <p className="text-sm text-gray-500">Pagamento instantâneo via QR Code</p>
              </div>
              {paymentMethod === 'PIX' && (
                <Check className="h-5 w-5 text-[#0d9488]" />
              )}
            </button>

            <button
              onClick={() => setPaymentMethod('BOLETO')}
              className={`w-full p-4 rounded-xl border-2 transition-all flex items-center gap-4 ${
                paymentMethod === 'BOLETO' 
                  ? 'border-[#0d9488] bg-[#0d9488]/5' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                paymentMethod === 'BOLETO' ? 'bg-[#0d9488]' : 'bg-gray-100'
              }`}>
                <Banknote className={`h-6 w-6 ${paymentMethod === 'BOLETO' ? 'text-white' : 'text-gray-500'}`} />
              </div>
              <div className="text-left flex-1">
                <p className="font-medium text-[#1e3a5f]">Boleto Bancário</p>
                <p className="text-sm text-gray-500">Vencimento em 3 dias úteis</p>
              </div>
              {paymentMethod === 'BOLETO' && (
                <Check className="h-5 w-5 text-[#0d9488]" />
              )}
            </button>

            <button
              onClick={() => setPaymentMethod('CREDIT_CARD')}
              className={`w-full p-4 rounded-xl border-2 transition-all flex items-center gap-4 ${
                paymentMethod === 'CREDIT_CARD' 
                  ? 'border-[#0d9488] bg-[#0d9488]/5' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                paymentMethod === 'CREDIT_CARD' ? 'bg-[#0d9488]' : 'bg-gray-100'
              }`}>
                <CreditCard className={`h-6 w-6 ${paymentMethod === 'CREDIT_CARD' ? 'text-white' : 'text-gray-500'}`} />
              </div>
              <div className="text-left flex-1">
                <p className="font-medium text-[#1e3a5f]">Cartão de Crédito</p>
                <p className="text-sm text-gray-500">Parcele em até 12x</p>
              </div>
              {paymentMethod === 'CREDIT_CARD' && (
                <Check className="h-5 w-5 text-[#0d9488]" />
              )}
            </button>
          </div>

          {/* Credit Card Form */}
          {paymentMethod === 'CREDIT_CARD' && (
            <div className="border-t pt-4 space-y-4">
              <h4 className="font-medium text-[#1e3a5f] flex items-center gap-2">
                <CreditCard className="h-4 w-4" />
                Dados do Cartão
              </h4>
              
              <div>
                <Label htmlFor="cc-holder">Nome no Cartão *</Label>
                <Input
                  id="cc-holder"
                  value={creditCardData.holderName}
                  onChange={(e) => setCreditCardData(prev => ({ ...prev, holderName: e.target.value.toUpperCase() }))}
                  placeholder="NOME COMO ESTÁ NO CARTÃO"
                />
              </div>
              
              <div>
                <Label htmlFor="cc-number">Número do Cartão *</Label>
                <Input
                  id="cc-number"
                  value={creditCardData.number}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').replace(/(\d{4})/g, '$1 ').trim();
                    setCreditCardData(prev => ({ ...prev, number: value }));
                  }}
                  placeholder="0000 0000 0000 0000"
                  maxLength={19}
                />
              </div>
              
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label htmlFor="cc-month">Mês *</Label>
                  <Input
                    id="cc-month"
                    value={creditCardData.expiryMonth}
                    onChange={(e) => setCreditCardData(prev => ({ ...prev, expiryMonth: e.target.value.replace(/\D/g, '').slice(0, 2) }))}
                    placeholder="MM"
                    maxLength={2}
                  />
                </div>
                <div>
                  <Label htmlFor="cc-year">Ano *</Label>
                  <Input
                    id="cc-year"
                    value={creditCardData.expiryYear}
                    onChange={(e) => setCreditCardData(prev => ({ ...prev, expiryYear: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
                    placeholder="AAAA"
                    maxLength={4}
                  />
                </div>
                <div>
                  <Label htmlFor="cc-cvv">CVV *</Label>
                  <Input
                    id="cc-cvv"
                    type="password"
                    value={creditCardData.ccv}
                    onChange={(e) => setCreditCardData(prev => ({ ...prev, ccv: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
                    placeholder="***"
                    maxLength={4}
                  />
                </div>
              </div>
              
              <h4 className="font-medium text-[#1e3a5f] flex items-center gap-2 pt-2">
                <Users className="h-4 w-4" />
                Dados do Titular
              </h4>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label htmlFor="holder-name">Nome Completo *</Label>
                  <Input
                    id="holder-name"
                    value={creditCardHolderInfo.name}
                    onChange={(e) => setCreditCardHolderInfo(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Nome completo do titular"
                  />
                </div>
                <div>
                  <Label htmlFor="holder-cpf">CPF/CNPJ *</Label>
                  <Input
                    id="holder-cpf"
                    value={creditCardHolderInfo.cpfCnpj}
                    onChange={(e) => setCreditCardHolderInfo(prev => ({ ...prev, cpfCnpj: e.target.value }))}
                    placeholder="000.000.000-00"
                  />
                </div>
                <div>
                  <Label htmlFor="holder-phone">Telefone *</Label>
                  <Input
                    id="holder-phone"
                    value={creditCardHolderInfo.phone}
                    onChange={(e) => setCreditCardHolderInfo(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="(00) 00000-0000"
                  />
                </div>
                <div>
                  <Label htmlFor="holder-cep">CEP *</Label>
                  <Input
                    id="holder-cep"
                    value={creditCardHolderInfo.postalCode}
                    onChange={(e) => setCreditCardHolderInfo(prev => ({ ...prev, postalCode: e.target.value }))}
                    placeholder="00000-000"
                  />
                </div>
                <div>
                  <Label htmlFor="holder-number">Número *</Label>
                  <Input
                    id="holder-number"
                    value={creditCardHolderInfo.addressNumber}
                    onChange={(e) => setCreditCardHolderInfo(prev => ({ ...prev, addressNumber: e.target.value }))}
                    placeholder="Nº"
                  />
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentModal(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={processPayment}
              className="bg-[#0d9488] hover:bg-[#0f766e]"
            >
              Continuar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PIX QR Code Modal */}
      <Dialog open={showPixModal} onOpenChange={setShowPixModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="h-5 w-5 text-[#0d9488]" />
              Pagamento via PIX
            </DialogTitle>
            <DialogDescription>
              Escaneie o QR Code ou copie o código para pagar
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {pixData?.qrCode && (
              <div className="flex justify-center">
                <div className="p-4 bg-white rounded-xl border-2 border-gray-100">
                  <img 
                    src={`data:image/png;base64,${pixData.qrCode}`}
                    alt="QR Code PIX"
                    className="w-48 h-48"
                  />
                </div>
              </div>
            )}

            <div className="text-center">
              <p className="text-2xl font-bold text-[#1e3a5f]">
                R$ {pixData?.value?.toFixed(2).replace('.', ',')}
              </p>
              <p className="text-sm text-gray-500">{pixData?.planName}</p>
            </div>

            {pixData?.copyPaste && (
              <div className="space-y-2">
                <Label>Código PIX Copia e Cola</Label>
                <div className="flex gap-2">
                  <Input 
                    value={pixData.copyPaste}
                    readOnly
                    className="text-xs"
                  />
                  <Button 
                    onClick={copyPixCode}
                    variant="outline"
                    className="shrink-0"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
              <p className="font-medium">Atenção:</p>
              <p>Após o pagamento, a confirmação pode levar alguns minutos. Você receberá um e-mail quando o pagamento for confirmado.</p>
            </div>
          </div>

          <DialogFooter>
            <Button 
              onClick={() => setShowPixModal(false)}
              className="w-full bg-[#0d9488] hover:bg-[#0f766e]"
            >
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Financial Data Modal */}
      <Dialog open={showFinancialModal} onOpenChange={setShowFinancialModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-[#0d9488]" />
              Dados Financeiros para Faturamento
            </DialogTitle>
            <DialogDescription>
              Preencha os dados da empresa para emissão de nota fiscal. Estes dados são necessários para adquirir um plano.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Razão Social / Nome Fantasia */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="razao_social">Razão Social *</Label>
                <Input
                  id="razao_social"
                  value={financialData.razao_social}
                  onChange={(e) => handleFinancialInputChange('razao_social', e.target.value)}
                  placeholder="Razão Social da empresa"
                />
              </div>
              <div>
                <Label htmlFor="nome_fantasia">Nome Fantasia</Label>
                <Input
                  id="nome_fantasia"
                  value={financialData.nome_fantasia}
                  onChange={(e) => handleFinancialInputChange('nome_fantasia', e.target.value)}
                  placeholder="Nome Fantasia"
                />
              </div>
            </div>

            {/* CNPJ */}
            <div className="space-y-2">
              <Label htmlFor="cnpj">CNPJ *</Label>
              <div className="relative">
                <Input
                  id="cnpj"
                  value={financialData.cnpj}
                  onChange={(e) => handleCNPJChange(e.target.value)}
                  placeholder="00.000.000/0000-00"
                  className={cnpjError ? 'border-red-500 focus:ring-red-500' : ''}
                  maxLength={18}
                />
                {cleanCNPJ(financialData.cnpj).length === 14 && !cnpjError && (
                  <Check className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-green-500" />
                )}
              </div>
              {cnpjError && (
                <p className="text-sm text-red-500 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {cnpjError}
                </p>
              )}
            </div>

            {/* Email para Nota Fiscal */}
            <div className="space-y-2">
              <Label htmlFor="email_nf">E-mail para Nota Fiscal *</Label>
              <Input
                id="email_nf"
                type="email"
                value={financialData.email_nf}
                onChange={(e) => handleFinancialInputChange('email_nf', e.target.value)}
                placeholder="financeiro@empresa.com.br"
              />
              <p className="text-xs text-gray-500">A nota fiscal será enviada para este e-mail</p>
            </div>

            {/* Inscrição Estadual */}
            <div className="space-y-2">
              <Label htmlFor="inscricao_estadual">Inscrição Estadual</Label>
              <div className="flex items-center gap-4">
                <Input
                  id="inscricao_estadual"
                  value={financialData.inscricao_estadual}
                  onChange={(e) => handleFinancialInputChange('inscricao_estadual', e.target.value)}
                  placeholder="Número da Inscrição Estadual"
                  disabled={financialData.inscricao_estadual_isento}
                  className={financialData.inscricao_estadual_isento ? 'bg-gray-100' : ''}
                />
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="ie_isento"
                    checked={financialData.inscricao_estadual_isento}
                    onCheckedChange={(checked) => {
                      handleFinancialInputChange('inscricao_estadual_isento', checked);
                      if (checked) {
                        handleFinancialInputChange('inscricao_estadual', '');
                      }
                    }}
                  />
                  <Label htmlFor="ie_isento" className="text-sm cursor-pointer">Isento</Label>
                </div>
              </div>
            </div>

            {/* Inscrição Municipal */}
            <div className="space-y-2">
              <Label htmlFor="inscricao_municipal">Inscrição Municipal</Label>
              <Input
                id="inscricao_municipal"
                value={financialData.inscricao_municipal}
                onChange={(e) => handleFinancialInputChange('inscricao_municipal', e.target.value)}
                placeholder="Número da Inscrição Municipal (CCM)"
              />
            </div>

            {/* Endereço */}
            <div className="border-t pt-4 mt-4">
              <h4 className="font-medium text-[#1e3a5f] flex items-center gap-2 mb-4">
                <MapPin className="h-4 w-4" />
                Endereço de Faturamento
              </h4>
              
              {/* CEP com busca automática - primeiro campo */}
              <div className="mb-4">
                <Label htmlFor="cep">CEP * <span className="text-xs text-gray-500">(Digite para buscar automaticamente)</span></Label>
                <div className="relative max-w-[200px]">
                  <Input
                    id="cep"
                    value={financialData.endereco_cep}
                    onChange={(e) => handleCEPChange(e.target.value)}
                    placeholder="00000-000"
                    className={cepError ? 'border-red-500 focus:ring-red-500 pr-10' : 'pr-10'}
                    maxLength={9}
                  />
                  {loadingCEP ? (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-[#0d9488]" />
                  ) : cleanCEP(financialData.endereco_cep).length === 8 && !cepError ? (
                    <Check className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-green-500" />
                  ) : (
                    <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  )}
                </div>
                {cepError && (
                  <p className="text-sm text-red-500 flex items-center gap-1 mt-1">
                    <AlertCircle className="h-3 w-3" />
                    {cepError}
                  </p>
                )}
              </div>
              
              <div className="grid grid-cols-4 gap-4">
                <div className="col-span-3">
                  <Label htmlFor="logradouro">Logradouro *</Label>
                  <Input
                    id="logradouro"
                    value={financialData.endereco_logradouro}
                    onChange={(e) => handleFinancialInputChange('endereco_logradouro', e.target.value)}
                    placeholder="Rua, Avenida, etc."
                  />
                </div>
                <div>
                  <Label htmlFor="numero">Número *</Label>
                  <Input
                    id="numero"
                    value={financialData.endereco_numero}
                    onChange={(e) => handleFinancialInputChange('endereco_numero', e.target.value)}
                    placeholder="Nº"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mt-4">
                <div>
                  <Label htmlFor="complemento">Complemento</Label>
                  <Input
                    id="complemento"
                    value={financialData.endereco_complemento}
                    onChange={(e) => handleFinancialInputChange('endereco_complemento', e.target.value)}
                    placeholder="Sala, Andar, etc."
                  />
                </div>
                <div>
                  <Label htmlFor="bairro">Bairro *</Label>
                  <Input
                    id="bairro"
                    value={financialData.endereco_bairro}
                    onChange={(e) => handleFinancialInputChange('endereco_bairro', e.target.value)}
                    placeholder="Bairro"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mt-4">
                <div>
                  <Label htmlFor="cidade">Cidade *</Label>
                  <Input
                    id="cidade"
                    value={financialData.endereco_cidade}
                    onChange={(e) => handleFinancialInputChange('endereco_cidade', e.target.value)}
                    placeholder="Cidade"
                  />
                </div>
                <div>
                  <Label htmlFor="estado">Estado *</Label>
                  <select
                    id="estado"
                    value={financialData.endereco_estado}
                    onChange={(e) => handleFinancialInputChange('endereco_estado', e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    <option value="">Selecione</option>
                    {ESTADOS_BR.map(uf => (
                      <option key={uf} value={uf}>{uf}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowFinancialModal(false);
                setSelectedPlanForPurchase(null);
              }}
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleSaveFinancialData}
              disabled={savingFinancial}
              className="bg-[#0d9488] hover:bg-[#0f766e]"
            >
              {savingFinancial ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Salvando...
                </>
              ) : (
                'Salvar e Continuar'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
